import PremiumPlusQuoteForm from '../components/PremiumPlusQuoteForm';
import { useSearchParams } from 'react-router-dom';
import { PREMIUM_SIZES } from '../utils/pergolaCatalog';
import React, { useEffect } from 'react';
import Navigation from '../components/Navigation';
import PergolaQuoteForm from '../components/PergolaQuoteForm';
import Footer from '../components/Footer';

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('PergolaQuoteForm Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-8">
          <h1 className="text-3xl font-bold text-red-600">Something went wrong</h1>
          <p className="mt-4">There was an error loading the pergola quote form.</p>
          <pre className="mt-4 p-4 bg-gray-100 rounded text-sm overflow-auto">
            {this.state.error?.toString()}
          </pre>
          <button
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded"
            onClick={() => window.location.reload()}
          >
            Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

const PergolaQuotePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  useEffect(() => {
    // Google Analytics
    const script1 = document.createElement('script');
    script1.async = true;
    script1.src = 'https://www.googletagmanager.com/gtag/js?id=G-SDHCNDS5MG';
    document.head.appendChild(script1);

    const script2 = document.createElement('script');
    script2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'G-SDHCNDS5MG');
    `;
    document.head.appendChild(script2);

    // Set document title and meta tags
    document.title = 'Get Your Pergola Quote | Evie Outdoors Ontario';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Design your perfect pergola and get an instant quote. Get a Premium estimate or request an individual Premium+ quote. Standard installation and delivery included; special site work quoted separately. Professional installation across Ontario with warranty included.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'pergola quote, pergola price, outdoor pergola Ontario, motorized pergola, louvered pergola, pergola installation, custom pergola');
    }

    // Add structured data for SEO
    const structuredData = document.createElement('script');
    structuredData.type = 'application/ld+json';
    structuredData.innerHTML = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Pergola Quote Service",
      "description": "Get an instant quote for custom pergolas with professional installation across Ontario",
      "provider": {
        "@type": "Organization",
        "name": "Evie Outdoors"
      },
      "offers": {
        "@type": "Offer",
        "priceRange": `${PREMIUM_SIZES[0].priceCAD}-${PREMIUM_SIZES[PREMIUM_SIZES.length - 1].priceCAD}`,
        "priceCurrency": "CAD",
        "availability": "https://schema.org/InStock"
      },
      "areaServed": {
        "@type": "State",
        "name": "Ontario"
      }
    });
    document.head.appendChild(structuredData);

    // Cleanup function
    return () => {
      document.head.removeChild(script1);
      document.head.removeChild(script2);
      document.head.removeChild(structuredData);
    };
  }, []);

  return (
    <div className="bg-gray-50 min-h-screen">
      <Navigation />
      <ErrorBoundary>
        {searchParams.get('model') === 'premium-plus' ? <PremiumPlusQuoteForm /> : <PergolaQuoteForm />}
      </ErrorBoundary>
      <Footer />
    </div>
  );
};

export default PergolaQuotePage;
